import type { DiagramKey, LinkItem } from '@/types/content'

export interface MegaLink extends LinkItem {
    diagram?: DiagramKey
}

export interface MegaColumn {
    title: string
    links: MegaLink[]
}

export interface NavFeature {
    title: string
    text: string
    image: string
    href: string
    cta: string
}

export interface NavGroup {
    label: string
    href: string
    note?: string
    feature: NavFeature
    columns: MegaColumn[]
}

export const AREA_LINKS: LinkItem[] = [
    { label: "Madison", href: '/service-areas/madison/', description: "Our home base" },
    { label: "Huntsville", href: '/service-areas/huntsville/', description: "Every week, every era of home" },
    { label: "Harvest", href: '/service-areas/harvest/', description: "Larger lots, mature trees" },
    { label: "Hazel Green & rural Madison County", href: '/service-areas/madison-county/', description: "Meridianville, New Market, Gurley, Owens Cross Roads" },
    { label: "Athens & Limestone County", href: '/service-areas/limestone-county/', description: "Tanner, Elkmont, Ardmore" },
    { label: "Decatur & Morgan County", href: '/service-areas/morgan-county/', description: "Plus Town Creek, Courtland, Hillsboro" },
    { label: "Florence & the Shoals", href: '/service-areas/lauderdale-county/', description: "Florence and Killen" },
    { label: "Scottsboro & Jackson County", href: '/service-areas/jackson-county/', description: "Ridge and hillside homes" },
    { label: "Guntersville & Marshall County", href: '/service-areas/marshall-county/', description: "Guntersville and Albertville" }
]

export const MAIN_NAV: NavGroup[] = [
    {
        label: "Exterior Windows & Doors",
        href: '/windows-doors/',
        note: "Exterior windows and exterior doors only. We do not install interior doors or interior windows.",
        feature: {
            title: "Exterior Windows & Doors",
            text: "Replacement windows and new exterior doors, measured and fitted by our own crews so every opening stays dry for years.",
            image: 'illustration-window',
            href: '/windows-doors/',
            cta: "Explore windows and doors"
        },
        columns: [
            {
                title: "Exterior windows",
                links: [
                    { label: "Exterior window replacement", href: '/services/window-replacement/', description: "How we replace and seal each opening" },
                    { label: "Double-hung", href: '/window-options/double-hung-windows/', diagram: 'double-hung' },
                    { label: "Casement", href: '/window-options/casement-windows/', diagram: 'casement' },
                    { label: "Sliding", href: '/window-options/sliding-windows/', diagram: 'sliding' },
                    { label: "Picture", href: '/window-options/picture-windows/', diagram: 'picture' },
                    { label: "Awning", href: '/window-options/awning-windows/', diagram: 'awning' },
                    { label: "Bay and bow", href: '/window-options/bay-and-bow-windows/', diagram: 'bay-bow' },
                    { label: "Garden", href: '/window-options/garden-windows/', diagram: 'garden' },
                    { label: "Exterior shutters", href: '/window-options/window-shutters/', diagram: 'shutters' }
                ]
            },
            {
                title: "Window materials",
                links: [
                    { label: "Vinyl windows", href: '/services/window-replacement/vinyl/', description: "The value benchmark" },
                    { label: "Fiberglass windows", href: '/services/window-replacement/fiberglass/', description: "Stable in heat, slim frames" },
                    { label: "Aluminum windows", href: '/services/window-replacement/aluminum/', description: "Strong and slim" },
                    { label: "Wood windows", href: '/services/window-replacement/wood/', description: "For homes where the look matters" },
                    { label: "Exterior storm windows", href: '/services/window-replacement/storm-windows/', description: "A second pane outside old windows" }
                ]
            },
            {
                title: "Exterior doors",
                links: [
                    { label: "Exterior door installation", href: '/services/door-installation/', description: "Set with a flashed sill pan" },
                    { label: "Entry doors", href: '/door-options/entry-doors/', diagram: 'entry-door' },
                    { label: "Patio and sliding doors", href: '/door-options/patio-doors/', diagram: 'patio-door' },
                    { label: "Exterior French doors", href: '/door-options/french-doors/', diagram: 'french-door' },
                    { label: "Storm doors", href: '/door-options/storm-doors/', diagram: 'storm-door' }
                ]
            }
        ]
    },
    {
        label: "Siding",
        href: '/services/siding/',
        feature: {
            title: "Siding",
            text: "Give your home a fresh face with durable siding, and the moisture protection behind it done right.",
            image: 'illustration-home-brick',
            href: '/services/siding/',
            cta: "Explore siding"
        },
        columns: [
            {
                title: "Siding",
                links: [
                    { label: "Siding overview", href: '/services/siding/', description: "Re-siding, repairs and what sits behind it" },
                    { label: "Fiber cement siding", href: '/services/siding/fiber-cement/', description: "Allura shake and lap" },
                    { label: "Soffit and fascia", href: '/services/siding/soffit-fascia/', description: "The edge where roof meets wall" }
                ]
            },
            {
                title: "Helpful guides",
                links: [
                    { label: "Fiber cement vs vinyl siding", href: '/blog/fiber-cement-vs-vinyl-siding/', description: "An honest comparison for Alabama" },
                    { label: "Siding replacement cost", href: '/blog/siding-replacement-cost-alabama/', description: "What moves the number" }
                ]
            }
        ]
    },
    {
        label: "Roofing",
        href: '/services/roofing/',
        feature: {
            title: "Roofing",
            text: "From worn shingles to a full replacement, start with a clear look at what your roof needs.",
            image: 'illustration-home-white',
            href: '/services/roofing/',
            cta: "Explore roofing"
        },
        columns: [
            {
                title: "Roofing",
                links: [
                    { label: "Roofing overview", href: '/services/roofing/', description: "Repair or replace, honestly" },
                    { label: "Roof replacement", href: '/services/roofing/roof-replacement/', description: "Tear-off, deck check, new roof" },
                    { label: "Roof ventilation", href: '/services/roofing/ventilation/', description: "Cooler attics, longer-lasting shingles" }
                ]
            },
            {
                title: "Helpful guides",
                links: [
                    { label: "What a new roof costs", href: '/blog/roof-replacement-cost-north-alabama/', description: "Squares, decking and access" },
                    { label: "Why your roof has black streaks", href: '/blog/black-streaks-on-roof/', description: "And how to clean it safely" }
                ]
            }
        ]
    },
    {
        label: "Pressure & Soft Washing",
        href: '/services/pressure-washing/',
        feature: {
            title: "Pressure & Soft Washing",
            text: "Soft washing for siding and roofs, pressure washing for concrete. Years of grime come off without damage.",
            image: 'job-house-wash',
            href: '/services/pressure-washing/',
            cta: "Explore washing"
        },
        columns: [
            {
                title: "Pressure & soft washing",
                links: [
                    { label: "Washing overview", href: '/services/pressure-washing/', description: "Which method suits which surface" },
                    { label: "House washing", href: '/services/pressure-washing/house-washing/', description: "Siding, trim and gutters" },
                    { label: "Soft washing", href: '/services/pressure-washing/soft-washing/', description: "Low pressure, deep clean" },
                    { label: "Roof washing", href: '/services/pressure-washing/roof-washing/', description: "Black streaks gone safely" },
                    { label: "Driveways and concrete", href: '/services/pressure-washing/driveway-concrete/', description: "Where real pressure belongs" },
                    { label: "Decks and fences", href: '/services/pressure-washing/deck-fence/', description: "A gentle hand on timber" },
                    { label: "Commercial washing", href: '/services/pressure-washing/commercial-washing/', description: "Storefronts, hotels, apartments" }
                ]
            }
        ]
    },
    {
        label: "Areas We Serve",
        href: '/service-areas/',
        feature: {
            title: "Areas we serve",
            text: "Based in Madison and working across North Alabama, from Florence to Scottsboro. We never charge for travel.",
            image: 'illustration-home-grey',
            href: '/service-areas/',
            cta: "All areas and towns"
        },
        columns: [
            {
                title: "Areas we serve",
                links: AREA_LINKS
            }
        ]
    }
]

export const SIMPLE_NAV: LinkItem[] = [
    { label: "Our Work", href: '/projects/' },
    { label: "Blog", href: '/blog/' },
    { label: "About", href: '/about/' }
]

export const FOOTER_COMPANY: LinkItem[] = [
    { label: "Our work", href: '/projects/' },
    { label: "About us", href: '/about/' },
    { label: "Reviews", href: '/reviews/' },
    { label: "Blog", href: '/blog/' },
    { label: "Free estimate", href: '/contact/' },
    { label: "Search the site", href: '/search/' },
    { label: "Privacy policy", href: '/privacy-policy/' },
    { label: "Terms of service", href: '/terms-of-service/' }
]
